import { Box, ButtonBase, Typography } from "@mui/material";
import { ReactNode } from "react";
import { radius } from "src/design/tokens";

export interface OptionCardProps {
  label: string;
  sub?: ReactNode;
  selected: boolean;
  onSelect: () => void;
  /** Optional leading visual, such as an `EventIcon`. */
  icon?: ReactNode;
  /** Show the design's radio indicator at the right edge (event options). */
  radio?: boolean;
}

/**
 * Selectable card button (aria-pressed), as the planner's class and event options. Selected:
 * 1.5 px primary border, a 3 px primary ring and the primary.light fill; with `radio`, a filled dot.
 */
export const OptionCard = ({
  label,
  sub,
  selected,
  onSelect,
  icon,
  radio = false,
}: OptionCardProps) => (
  <ButtonBase
    aria-pressed={selected}
    onClick={onSelect}
    focusRipple
    sx={(theme) => ({
      width: "100%",
      minHeight: radio ? 76 : 0,
      p: 1.75,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      gap: 1.5,
      textAlign: "left",
      borderRadius: `${radius.panel}px`,
      border: "1.5px solid",
      borderColor: selected
        ? theme.palette.primary.main
        : theme.palette.divider,
      boxShadow: selected ? `0 0 0 3px ${theme.palette.primary.light}` : "none",
      bgcolor: selected
        ? theme.palette.primary.light
        : theme.palette.background.paper,
      color: theme.palette.text.primary,
      transition: theme.transitions.create(
        ["border-color", "background-color", "box-shadow"],
        {
          duration: theme.transitions.duration.shortest,
        },
      ),
      "&:hover": {
        borderColor: selected
          ? theme.palette.primary.main
          : theme.palette.inputBorder,
      },
      "&.Mui-focusVisible": {
        outline: `2px solid ${theme.palette.focusRing}`,
        outlineOffset: 2,
      },
    })}
  >
    {icon}
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        flex: 1,
        minWidth: 0,
      }}
    >
      <Typography
        component="span"
        sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      {sub ? (
        <Typography
          component="span"
          sx={{ fontSize: 12, lineHeight: 1.3, color: "text.secondary" }}
        >
          {sub}
        </Typography>
      ) : null}
    </Box>
    {radio ? (
      <Box
        aria-hidden
        data-testid="option-radio"
        sx={(theme) => ({
          width: 18,
          height: 18,
          flex: "none",
          borderRadius: "50%",
          border: `2px solid ${
            selected ? theme.palette.primary.main : theme.palette.inputBorder
          }`,
          display: "grid",
          placeItems: "center",
        })}
      >
        <Box
          sx={(theme) => ({
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: selected ? theme.palette.primary.main : "transparent",
          })}
        />
      </Box>
    ) : null}
  </ButtonBase>
);

export default OptionCard;
